import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';

/**
 * Hook to check if user has completed onboarding
 *
 * GET /api/onboarding/check
 *
 * Returns: { hasOrganization: boolean, organizationId?: string }
 *
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @param {number} options.staleTime - Time in ms before data is considered stale
 * @param {boolean} options.refetchOnMount - Whether to refetch on component mount
 *
 * @returns {Object} Query object with data, isLoading, error, refetch
 *
 * @example
 * const { data, isLoading, refetch } = useCheckOnboarding();
 *
 * if (data?.hasOrganization) {
 *   // User has completed onboarding
 * }
 */
const useCheckOnboarding = (options = {}) => {
  const {
    enabled = true,
    staleTime = 60000, // 1 minute
    refetchOnMount = true,
  } = options;

  return useQuery({
    queryKey: ['onboardingCheck'],
    queryFn: async () => {
      const response = await http.get('/onboarding/check');
      return response.data;
    },
    enabled,
    staleTime,
    refetchOnMount,
    retry: 1, // Only retry once on failure
  });
};

export { useCheckOnboarding };
export default useCheckOnboarding;
